import {Button} from 'antd'
import React from 'react'
import {IoLockClosed} from 'react-icons/io5'
import {BsArrowLeft} from 'react-icons/bs'
import {ItemInformation} from 'components/common/ItemInformation'
import LookupField from 'components/LookupField'
import useTranslate from 'modules/local/useTranslate'
import {renderIf} from 'views/Shared'
import {showConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import {AssociationChangeRequestButton} from './AssociationStatusHeader'

// ─────────────────────────────────────────────────────────────────────────────
// Panel bên trái - 2 chế độ:
//  - Wizard khai báo (status='empty', hoặc đang bấm "Sửa yêu cầu" từ pending/connected)
//  - Thẻ thông tin đã khoá, chỉ đọc (status='pending'|'connected'|'rejected' & không đang sửa)
//
// CHỈ 1 bước tìm & chọn trực tiếp (không có bước lọc Giáo phận -> Giáo hạt riêng trước đó) - mỗi
// dòng kết quả tìm kiếm đã tự hiển thị sẵn Giáo phận/Giáo hạt của nó (xem renderOption ở
// OrganizationSettingAssociations.js: parish hiện `( diocese_fullname )` ngay trong dòng), nên thêm
// bước lọc trung gian là thừa - test thực tế cho thấy vậy, bỏ hẳn wizard nhiều bước trước đó.
// ─────────────────────────────────────────────────────────────────────────────

const AssociationDeclareWizard = ({
                                       form,
                                       name,
                                       associationTypeValue,
                                       renderOption,
                                       onCancelEdit,
                                       showCancelEdit,
                                   }) => {
    const t = useTranslate()
    const association = form?.values?.association

    const applySelection = (e) => {
        // e.target.id rỗng = người dùng bấm nút "x" xoá lựa chọn trên LookupField (không phải chọn
        // 1 kết quả) - phải clear thật (association=null) chứ không lưu nguyên {name, value: null}
        // của event, nếu không "association" vẫn truthy -> sai trạng thái "chưa liên kết".
        if (!e.target?.id) {
            form.setValues({
                ...form.values,
                association: null,
                association_name: null,
                association_id: null,
                association_type: null,
            })
            return
        }
        form.setValues({
            ...form.values,
            association: e.target,
            association_name: e.target.value,
            association_id: e.target.id,
            association_type: e.target._type,
        })
    }

    return (
        <div className="p-4 background shadow-md rounded-xl flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <div>
                    <h2 className="font-bold text-lg text-color-800 tracking-wide">
                        {t('declare your position in the system')}
                    </h2>
                    <div className="my-1 w-16 h-1 rounded-full bg-secondary-400"/>
                </div>
                {/* Sửa/Hủy yêu cầu: luôn xuống dòng riêng dưới tiêu đề, không chen chung 1 hàng - dễ vỡ
                    layout khi tiêu đề + 2 nút không đủ chỗ trên màn hình hẹp. */}
                {renderIf(
                    showCancelEdit,
                    <div className="flex flex-wrap items-center gap-2">
                        <Button size="small" danger type="text"
                                className="button-rounded-md no-border" onClick={() => confirmCancelAssociationRequest({t, form})}>
                            {t('cancel this link request')}
                        </Button>
                        <Button size="small" icon={<BsArrowLeft size={13} className="mr-1"/>}
                                className="button-rounded-md no-border background-100" onClick={onCancelEdit}>
                            {t('back')}
                        </Button>
                    </div>
                )}
            </div>

            <LookupField
                title="choose link"
                placeholder={t('click to select')}
                createIfNotExists={false}
                defaultValue={form?.values?.association_name}
                selectedOption={association}
                apiPath={`/lookup/${associationTypeValue}/get-by-types`}
                name={name}
                withInput={() => ({size: 'large'})}
                OptionWrapper={({children, ...props}) => (
                    <div className="flex items-center gap-2 border border-color-50 hover:shadow-md rounded-lg relative" {...props}>
                        {children}
                    </div>
                )}
                renderOption={(o) => renderOption(associationTypeValue, o, true)}
                handleChange={applySelection}
            />

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-color-50">
                <Button
                    type="primary"
                    loading={form?.isSubmitting}
                    disabled={!association || form?.isSubmitting}
                    className="button-rounded-md no-border px-6 font-semibold"
                    onClick={() => {
                        // Cùng guard chống double-submit như FormActionBar.submit() - Formik tự quản
                        // lý isSubmitting, không tự tắt sớm ở đây.
                        if (form?.isSubmitting) return
                        form.handleSubmit()
                    }}>
                    {t('send link request')}
                </Button>
            </div>
        </div>
    )
}

const AssociationLockedSummary = ({
                                       association,
                                       associationTypeValue,
                                       pathLabel,
                                       status,
                                       isSubmitting,
                                       onStartEdit,
                                   }) => {
    const t = useTranslate()
    return (
        <div className="p-4 background shadow-md rounded-xl flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h2 className="font-bold text-lg text-color-800 tracking-wide">
                    {t('declared information')}
                </h2>
                {/* alignSelf inline: xem giải thích ở AssociationStatusHeader.js - class self-start
                    không đáng tin cậy ở đây, set thẳng inline style cho chắc. */}
                <span
                    style={{alignSelf: 'flex-start'}}
                    className="flex items-center gap-1 px-2 py-1 rounded-full background-100 text-color-400 font-medium text-2xs whitespace-nowrap">
                    <IoLockClosed size={11}/>
                    {t('update locked')}
                </span>
            </div>

            {renderIf(
                pathLabel,
                <ItemInformation label="linked parent organization" value={pathLabel}/>
            )}
            <ItemInformation label="registered representative name" value={association?.fullname || association?.name}/>
            <ItemInformation
                label="record status"
                value={status === 'connected'
                    ? t('approved by admin')
                    : status === 'rejected'
                        ? t('request rejected')
                        : t('in the system approval queue')}
            />

            {renderIf(
                status === 'connected',
                <div className="pt-2 border-t border-color-50">
                    <AssociationChangeRequestButton loading={isSubmitting} onClick={onStartEdit}/>
                </div>
            )}
        </div>
    )
}

const AssociationDeclarePanel = ({
                                      form,
                                      name,
                                      status,
                                      associationTypeValue,
                                      renderOption,
                                      pathLabel,
                                      isSubmitting,
                                      isEditing,
                                      setIsEditing,
                                  }) => {
    const association = form?.values?.association
    const showWizard = status === 'empty' || isEditing

    if (showWizard) {
        return (
            <AssociationDeclareWizard
                form={form}
                name={name}
                associationTypeValue={associationTypeValue}
                renderOption={renderOption}
                showCancelEdit={isEditing}
                onCancelEdit={() => {
                    form.resetForm()
                    setIsEditing(false)
                }}
            />
        )
    }

    return (
        <AssociationLockedSummary
            association={association}
            associationTypeValue={associationTypeValue}
            pathLabel={pathLabel}
            status={status}
            isSubmitting={isSubmitting}
            onStartEdit={() => setIsEditing(true)}
        />
    )
}

// "Hủy yêu cầu" (dùng ở banner pending) - xoá association rồi submit ngay, có xác nhận trước vì đây
// là thao tác phá dữ liệu đã khai báo. Đặt ở đây (cùng file) để tái dùng chung state clearAssociation
// logic thay vì viết lại ở orchestrator.
export const confirmCancelAssociationRequest = ({t, form}) =>
    showConfirmDialog({
        translate: t,
        title: t('cancel this link request'),
        message: t('are you sure you want to cancel this link request'),
        onOk: () => {
            form.setValues({
                ...form.values,
                association: null,
                association_name: null,
                association_id: null,
                association_type: null,
            })
            setTimeout(() => form.handleSubmit(), 0)
        },
    })

export default AssociationDeclarePanel
